/**
 * Column Sorting Utilities
 *
 * Derives column order from form_section.order and form_order metadata.
 * This ensures consistency between table columns and form layout.
 */

import type { FieldMetadata, FormSection } from '@/services/metadataService'

/**
 * Get fields that should appear in default table view.
 *
 * @param fields - All field metadata
 * @returns Array of field keys where table_default is true
 */
export function getDefaultTableFields(
  fields: Record<string, FieldMetadata>
): string[] {
  return Object.entries(fields)
    .filter(([, field]) => field.table_default === true)
    .map(([key]) => key)
}

/**
 * Get all visible (non-system) fields.
 *
 * @param fields - All field metadata
 * @returns Array of field keys where is_visible is not false
 */
export function getVisibleFields(
  fields: Record<string, FieldMetadata>
): string[] {
  return Object.entries(fields)
    .filter(([, field]) => field.is_visible !== false)
    .map(([key]) => key)
}

/**
 * Derive column order from form_section.order and form_order.
 *
 * Algorithm:
 * 1. Sort form_sections by order
 * 2. For each section, get fields sorted by form_order
 * 3. Append orphan fields (no form_section) at the end
 *
 * @param fields - All field metadata
 * @param formSections - Form section definitions
 * @param filterFn - Optional filter function (default: table_default === true)
 * @returns Ordered array of field keys
 */
export function deriveColumnOrder(
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[],
  filterFn: (key: string, field: FieldMetadata) => boolean = (_, field) =>
    field.table_default === true
): string[] {
  // If no form sections provided, return all fields that pass the filter
  // sorted by their order property (fallback for endpoints that don't return form_sections)
  if (!formSections || formSections.length === 0) {
    return Object.entries(fields)
      .filter(([key, field]) => filterFn(key, field))
      .sort((a, b) => (a[1].order ?? a[1].form_order ?? 1000) - (b[1].order ?? b[1].form_order ?? 1000))
      .map(([key]) => key)
  }

  // Sort sections by order
  const sortedSections = [...formSections].sort((a, b) => a.order - b.order)

  // Build ordered column list from sections
  const orderedColumns: string[] = []
  const assignedFields = new Set<string>()

  for (const section of sortedSections) {
    // Get fields in this section that pass the filter
    const sectionFields = Object.entries(fields)
      .filter(
        ([key, field]) =>
          field.form_section === section.name && filterFn(key, field)
      )
      .sort((a, b) => (a[1].form_order ?? 0) - (b[1].form_order ?? 0))
      .map(([key]) => key)

    orderedColumns.push(...sectionFields)
    sectionFields.forEach((key) => assignedFields.add(key))
  }

  // Append orphan fields (no form_section but pass filter)
  const orphanFields = Object.entries(fields)
    .filter(
      ([key, field]) => !field.form_section && filterFn(key, field) && !assignedFields.has(key)
    )
    .map(([key]) => key)

  orderedColumns.push(...orphanFields)

  return orderedColumns
}

/**
 * Get default column order (table_default fields in form order).
 */
export function getDefaultColumnOrder(
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[]
): string[] {
  return deriveColumnOrder(fields, formSections)
}

/**
 * Get all column order (all visible fields in form order).
 */
export function getAllColumnOrder(
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[]
): string[] {
  return deriveColumnOrder(fields, formSections, (_, field) => field.is_visible !== false)
}

/**
 * Get recommended column order (recommended fields in form order).
 */
export function getRecommendedColumnOrder(
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[]
): string[] {
  return deriveColumnOrder(fields, formSections, (_, field) => field.recommended === true)
}

/**
 * Apply preset view to get visible columns.
 *
 * NOTE: This function expects the caller to pass the appropriate pre-filtered array:
 * - For 'essential': pass defaultColumns (table_default=true fields)
 * - For 'standard': pass recommendedColumns (recommended=true fields)
 * - For 'complete': pass allColumns (all visible fields)
 *
 * The function simply returns the array as-is since filtering should happen at call site
 * based on metadata flags (table_default, recommended).
 *
 * @param columns - Pre-filtered columns appropriate for the preset
 * @param preset - Preset view being applied (used for documentation, not filtering)
 * @returns The columns array as-is
 */
export function applyPresetView(
  columns: string[],
  preset: 'essential' | 'standard' | 'complete'
): string[] {
  // Caller is responsible for passing the correct filtered array
  // Essential = table_default fields, Standard = recommended fields, Complete = all fields
  void preset // Acknowledge param even though we don't use it (preset selection happens at call site)
  return columns
}

/**
 * Merge user preferences with available columns.
 * - Preserves user's column order for existing columns
 * - Removes columns that no longer exist in metadata
 * - Appends new columns from metadata (that user hasn't seen yet)
 *
 * This ensures the cache auto-adapts when metadata changes without
 * requiring manual cache invalidation or version tracking.
 *
 * @param savedColumns - User's saved column preferences
 * @param availableColumns - Currently available columns from metadata
 * @returns Merged columns: user's order + new columns appended
 */
export function mergeColumnPreferences(
  savedColumns: string[],
  availableColumns: string[]
): string[] {
  const availableSet = new Set(availableColumns)
  const savedSet = new Set(savedColumns)

  // Keep saved columns that still exist (preserves user's order)
  const validSaved = savedColumns.filter((col) => availableSet.has(col))

  // Find new columns in metadata that user hasn't seen yet
  const newColumns = availableColumns.filter((col) => !savedSet.has(col))

  // Return user's columns + new columns appended at end
  return [...validSaved, ...newColumns]
}
